import React, { useState, useEffect } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, faSpinner, faPlus, faTrash, faDownload,
  faFileAudio, faVideo, faPlay, faPause, faEllipsisV
} from '@fortawesome/free-solid-svg-icons';
import { 
  Card, Form, Button, Table, Alert, Badge, Modal, 
  Row, Col, Dropdown, InputGroup
} from '@themesberg/react-bootstrap';
import { 
  collection, getDocs, getDoc, doc, 
  addDoc, deleteDoc, serverTimestamp
} from 'firebase/firestore';
import { uploadToR2, deleteFromR2, generateUUID } from '../../utils/r2Upload';
import {  db } from '../../config/firebase';

export default () => {
  const { userId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const type = queryParams.get('type') || 'audio'; // 'audio' or 'video'

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [userInfo, setUserInfo] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [recordingTitle, setRecordingTitle] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const [audioElement, setAudioElement] = useState(null);
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0);
  const [showSpeedModal, setShowSpeedModal] = useState(false);
  const [selectedRecordingForSpeed, setSelectedRecordingForSpeed] = useState(null);

  useEffect(() => {
    if (userId) {
      loadData();
    }
  }, [userId, type]);

  useEffect(() => {
    // Cleanup audio element on unmount
    return () => {
      if (audioElement) {
        audioElement.pause();
        audioElement.src = '';
      }
    };
  }, [audioElement]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Kullanıcı bilgilerini al
      const userDoc = await getDoc(doc(db, 'users', userId));
      const userData = userDoc.exists() ? userDoc.data() : {};
      setUserInfo({
        id: userId,
        name: userData.name || 'Bilinmeyen',
        profileImageUrl: userData.profileImageUrl || null
      });

      // Kayıtları yükle
      await loadRecordings();
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Veriler yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const loadRecordings = async () => {
    try {
      const collectionName = type === 'audio' ? 'audioRecordings' : 'videoRecordings';
      const recordingsRef = collection(db, 'users', userId, collectionName);
      const snapshot = await getDocs(recordingsRef);
      const recordingsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRecordings(recordingsList.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
        return dateB - dateA; // Yeni olan üstte
      }));
    } catch (err) {
      console.error('Error loading recordings:', err);
      setError('Kayıtlar yüklenirken bir hata oluştu.');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Dosya tipi kontrolü
    if (type === 'audio' && !file.type.startsWith('audio/')) {
      setError('Lütfen bir ses dosyası seçin (MP3, M4A, vb.).');
      return;
    }
    if (type === 'video' && !file.type.startsWith('video/')) {
      setError('Lütfen bir video dosyası seçin (MP4, vb.).');
      return;
    }

    setSelectedFile(file);
    setError(null);
  };

  const handleUpload = async () => {
    if (!selectedFile || !recordingTitle.trim()) {
      setError('Lütfen başlık girin ve dosya seçin.');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      // Storage'a yükle
      const fileExtension = selectedFile.name.split('.').pop();
      const fileName = `${Date.now()}_${recordingTitle.replace(/[^a-zA-Z0-9]/g, '_')}.${fileExtension}`;
      const storagePath = type === 'audio' 
        ? `test_users/${userId}/audio/${fileName}`
        : `test_users/${userId}/video/${fileName}`;
      const downloadUrl = await uploadToR2(selectedFile, storagePath);

      // Firestore'a metadata kaydet
      const collectionName = type === 'audio' ? 'audioRecordings' : 'videoRecordings';
      const recordingsRef = collection(db, 'users', userId, collectionName);
      await addDoc(recordingsRef, {
        title: recordingTitle.trim(),
        fileUrl: downloadUrl,
        fileName: fileName,
        fileSize: selectedFile.size,
        createdAt: serverTimestamp()
      });

      // Listeyi yenile
      await loadRecordings();
      
      // Modal'ı kapat ve formu temizle
      setShowAddModal(false);
      setRecordingTitle("");
      setSelectedFile(null);
      document.getElementById('fileInput').value = '';
    } catch (err) {
      console.error('Error uploading recording:', err);
      setError('Kayıt yüklenirken bir hata oluştu.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (recording) => {
    if (!window.confirm(`"${recording.title}" kaydını silmek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      // R2'den sil
      try {
        await deleteFromR2(recording.fileUrl);
      } catch (err) {
        console.warn('R2 delete error (file may not exist):', err);
      }

      // Firestore'dan sil
      const collectionName = type === 'audio' ? 'audioRecordings' : 'videoRecordings';
      await deleteDoc(doc(db, 'users', userId, collectionName, recording.id));

      // Listeyi yenile
      await loadRecordings();
    } catch (err) {
      console.error('Error deleting recording:', err);
      setError('Kayıt silinirken bir hata oluştu.');
    }
  };

  const handlePlay = (recording) => {
    if (playingId === recording.id) {
      // Duraklat
      if (audioElement) {
        audioElement.pause();
        setPlayingId(null);
        setAudioElement(null);
      }
    } else {
      // Oynat
      if (audioElement) {
        audioElement.pause();
      }
      const audio = new Audio(recording.fileUrl);
      audio.playbackRate = playbackSpeed;
      audio.play();
      setAudioElement(audio);
      setPlayingId(recording.id);

      audio.onended = () => {
        setPlayingId(null);
        setAudioElement(null);
      };
    }
  };

  const handleDownload = (recording) => {
    window.open(recording.fileUrl, '_blank');
  };

  const handleSpeedChange = (recording) => {
    setSelectedRecordingForSpeed(recording);
    setShowSpeedModal(true);
  };

  const applySpeedChange = () => {
    if (audioElement && playingId === selectedRecordingForSpeed?.id) {
      audioElement.playbackRate = playbackSpeed;
    }
    setShowSpeedModal(false);
    setSelectedRecordingForSpeed(null);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <FontAwesomeIcon icon={faSpinner} className="fa-spin fa-2x" />
        <p className="mt-3">Yükleniyor...</p>
      </div>
    );
  }

  return (
    <>
      <div className="py-4">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center">
            <Button variant="secondary" onClick={() => history.goBack()} className="me-3">
              <FontAwesomeIcon icon={faArrowLeft} /> Geri
            </Button>
            <div>
              <h4 className="mb-0">
                {type === 'audio' ? (
                  <>
                    <FontAwesomeIcon icon={faFileAudio} className="me-2" />
                    Ses Kayıtları
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faVideo} className="me-2" />
                    Video Kayıtları
                  </>
                )}
              </h4>
              {userInfo && (
                <small className="text-muted">Kullanıcı: {userInfo.name}</small>
              )}
            </div>
          </div>
          <Button 
            variant="primary"
            onClick={() => {
              setRecordingTitle("");
              setSelectedFile(null);
              setError(null);
              setShowAddModal(true);
            }}
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            {type === 'audio' ? 'Ses Kaydı Ekle' : 'Video Kaydı Ekle'}
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" className="mb-4" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card className="border-0 shadow-sm">
        <Card.Body>
          {recordings.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted">
                {type === 'audio' 
                  ? 'Henüz hiç ses kaydı eklenmemiş.' 
                  : 'Henüz hiç video kaydı eklenmemiş.'}
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table className="table-dark table-hover mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>#</th>
                    <th>Başlık</th>
                    <th style={{ width: '120px' }}>Dosya Boyutu</th>
                    <th style={{ width: '180px' }}>Tarih</th>
                    <th style={{ width: '200px' }}>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {recordings.map((recording, index) => (
                    <tr key={recording.id}>
                      <td className="text-center">{index + 1}</td>
                      <td>{recording.title || 'İsimsiz'}</td>
                      <td>{formatFileSize(recording.fileSize)}</td>
                      <td>{formatDate(recording.createdAt)}</td>
                      <td>
                        <div className="d-flex gap-1">
                          {type === 'audio' && (
                            <>
                              <Button
                                variant={playingId === recording.id ? "warning" : "success"}
                                size="sm"
                                onClick={() => handlePlay(recording)}
                                title={playingId === recording.id ? "Duraklat" : "Oynat"}
                              >
                                <FontAwesomeIcon icon={playingId === recording.id ? faPause : faPlay} />
                              </Button>
                              <Dropdown>
                                <Dropdown.Toggle
                                  variant="outline-secondary"
                                  size="sm"
                                  id={`dropdown-${recording.id}`}
                                >
                                  <FontAwesomeIcon icon={faEllipsisV} />
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                  <Dropdown.Item onClick={() => handleDownload(recording)}>
                                    <FontAwesomeIcon icon={faDownload} className="me-2" />
                                    İndir
                                  </Dropdown.Item>
                                  <Dropdown.Item onClick={() => handleSpeedChange(recording)}>
                                    Oynatma Hızı
                                  </Dropdown.Item>
                                  <Dropdown.Divider />
                                  <Dropdown.Item 
                                    onClick={() => handleDelete(recording)}
                                    className="text-danger"
                                  >
                                    <FontAwesomeIcon icon={faTrash} className="me-2" />
                                    Sil
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </>
                          )}
                          {type === 'video' && (
                            <>
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => window.open(recording.fileUrl, '_blank')}
                                title="İzle"
                              >
                                <FontAwesomeIcon icon={faPlay} />
                              </Button>
                              <Dropdown>
                                <Dropdown.Toggle
                                  variant="outline-secondary"
                                  size="sm"
                                  id={`dropdown-${recording.id}`}
                                >
                                  <FontAwesomeIcon icon={faEllipsisV} />
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                  <Dropdown.Item onClick={() => handleDownload(recording)}>
                                    <FontAwesomeIcon icon={faDownload} className="me-2" />
                                    İndir
                                  </Dropdown.Item>
                                  <Dropdown.Divider />
                                  <Dropdown.Item 
                                    onClick={() => handleDelete(recording)}
                                    className="text-danger"
                                  >
                                    <FontAwesomeIcon icon={faTrash} className="me-2" />
                                    Sil
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Kayıt Ekleme Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {type === 'audio' ? 'Ses Kaydı Ekle' : 'Video Kaydı Ekle'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Başlık (Panel İçin)</Form.Label>
            <Form.Control
              type="text"
              value={recordingTitle}
              onChange={(e) => setRecordingTitle(e.target.value)}
              placeholder="Örn: Selamlaşma Kaydı"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Dosya Seç</Form.Label>
            <Form.Control
              type="file"
              accept={type === 'audio' ? 'audio/*' : 'video/*'}
              onChange={handleFileSelect}
              id="fileInput"
            />
            {selectedFile && (
              <small className="text-muted d-block mt-2">
                Seçilen dosya: {selectedFile.name} ({formatFileSize(selectedFile.size)})
              </small>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            İptal
          </Button>
          <Button 
            variant="primary" 
            onClick={handleUpload}
            disabled={!recordingTitle.trim() || !selectedFile || uploading}
          >
            {uploading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="fa-spin me-2" />
                Yükleniyor...
              </>
            ) : (
              'Yükle'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Oynatma Hızı Modal (Sadece ses için) */}
      {type === 'audio' && (
        <Modal show={showSpeedModal} onHide={() => setShowSpeedModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Oynatma Hızı</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>Hız: {playbackSpeed}x</Form.Label>
              <Form.Range
                min="0.5"
                max="2"
                step="0.1"
                value={playbackSpeed}
                onChange={(e) => setPlaybackSpeed(parseFloat(e.target.value))}
              />
              <div className="d-flex justify-content-between mt-2">
                <small>0.5x</small>
                <small>1.0x</small>
                <small>1.5x</small>
                <small>2.0x</small>
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowSpeedModal(false)}>
              İptal
            </Button>
            <Button variant="primary" onClick={applySpeedChange}>
              Uygula
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

